import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import z from "zod";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";

interface SessionFeedbackFormProps {
  studentName: string;
  tutorName: string;
  eventId: number;
  onSubmit: (data: SessionFeedbackData) => Promise<void>;
  onCancel: () => void;
}

export const SessionFeedbackDataSchema = z.object({
  // Required fields
  event_id: z.number().int(),
  tutor_name: z.string().min(1, "Tutor name is required."),
  student_name: z.string().min(1, "Student name is required."),
  session_rating: z.number().int().min(1).max(5, "Session rating must be between 1 and 5."), // Assuming a 1-5 rating scale

  // Rating 1 (Poor) related fields
  poor_reason: z.string().optional(),
  has_phone_number: z.string().optional(),
  current_grade: z.string().optional(),
  giving_homework: z.string().optional(),
  performance_rating: z.number().int().min(1).max(10).optional(), // Assuming a 1-5 rating

  // Rating 2 (Below Average) related fields
  speak_with_parents: z.string().optional(),
  student_asks_questions: z.string().optional(),

  // Rating 3 (Average) related fields
  concerns: z.string().optional(),
});

export interface SessionFeedbackData {
  event_id: number;
  tutor_name: string;
  student_name: string;
  session_rating: number;
  // Rating 1 (Poor) fields
  poor_reason?: string;
  has_phone_number?: string;
  current_grade?: string;
  giving_homework?: string;
  performance_rating?: number;
  // Rating 2 (Below Average) fields
  speak_with_parents?: string;
  student_asks_questions?: string;
  // Rating 3 (Average) fields
  concerns?: string;
}

type FormValues = Omit<SessionFeedbackData, "event_id" | "tutor_name" | "student_name">;

export function SessionFeedbackForm({
  studentName,
  tutorName,
  eventId,
  onSubmit,
  onCancel,
}: SessionFeedbackFormProps) {
  const form = useForm<FormValues>({
    defaultValues: {
      session_rating: undefined,
    },
  });

  const sessionRating = form.watch("session_rating");

  const handleSubmit = async (data: FormValues) => {
    const fullData: SessionFeedbackData = {
      event_id: eventId,
      tutor_name: tutorName,
      student_name: studentName,
      ...data,
    };
    await onSubmit(fullData);
  };

  const renderRatingSection = () => {
    if (!sessionRating) return null;

    switch (sessionRating) {
      case 1:
        return (
          <div className="space-y-4 animate-in fade-in-50">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please reach out to Matt at 516-643-4126 to setup a meeting ASAP.
              </AlertDescription>
            </Alert>

            <FormField
              control={form.control}
              name="poor_reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Why did you rate the session poor?</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Explain what went wrong..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="has_phone_number"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Do they have your phone number so they can ask you questions at any time?
                  </FormLabel>
                  <FormControl>
                    <RadioGroup onValueChange={field.onChange} value={field.value}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="phone-1-yes" />
                        <FormLabel htmlFor="phone-1-yes" className="font-normal">
                          Yes
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="phone-1-no" />
                        <FormLabel htmlFor="phone-1-no" className="font-normal">
                          No
                        </FormLabel>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="current_grade"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What is the student's current grade?</FormLabel>
                  <FormDescription>If you don't know, find out by the next session</FormDescription>
                  <FormControl>
                    <Input placeholder="e.g., B+" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="giving_homework"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Are you giving them homework?</FormLabel>
                  <FormControl>
                    <RadioGroup onValueChange={field.onChange} value={field.value}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="hw-1-yes" />
                        <FormLabel htmlFor="hw-1-yes" className="font-normal">
                          Yes
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="hw-1-no" />
                        <FormLabel htmlFor="hw-1-no" className="font-normal">
                          No
                        </FormLabel>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="performance_rating"
              rules={{ required: "This field is required", min: 1, max: 10 }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rate your overall performance (1-10)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-4 animate-in fade-in-50">
            <FormField
              control={form.control}
              name="giving_homework"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Are you giving them homework?</FormLabel>
                  <FormControl>
                    <RadioGroup onValueChange={field.onChange} value={field.value}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="hw-2-yes" />
                        <FormLabel htmlFor="hw-2-yes" className="font-normal">
                          Yes
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="hw-2-no" />
                        <FormLabel htmlFor="hw-2-no" className="font-normal">
                          No
                        </FormLabel>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="has_phone_number"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Do they have your phone number so they can ask you questions at any time?
                  </FormLabel>
                  <FormControl>
                    <RadioGroup onValueChange={field.onChange} value={field.value}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="phone-2-yes" />
                        <FormLabel htmlFor="phone-2-yes" className="font-normal">
                          Yes
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="phone-2-no" />
                        <FormLabel htmlFor="phone-2-no" className="font-normal">
                          No
                        </FormLabel>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="speak_with_parents"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Do you ever speak with the parents?</FormLabel>
                  <FormControl>
                    <Input placeholder="Yes, weekly / No, never / etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="student_asks_questions"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Does the student ask questions during the session?</FormLabel>
                  <FormControl>
                    <RadioGroup onValueChange={field.onChange} value={field.value}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="questions-yes" />
                        <FormLabel htmlFor="questions-yes" className="font-normal">
                          Yes
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="questions-no" />
                        <FormLabel htmlFor="questions-no" className="font-normal">
                          No
                        </FormLabel>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="performance_rating"
              rules={{ required: "This field is required", min: 1, max: 10 }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rate your overall performance (1-10)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );

      case 3:
        return (
          <div className="space-y-4 animate-in fade-in-50">
            <FormField
              control={form.control}
              name="giving_homework"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Are you giving them homework?</FormLabel>
                  <FormControl>
                    <RadioGroup onValueChange={field.onChange} value={field.value}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="hw-3-yes" />
                        <FormLabel htmlFor="hw-3-yes" className="font-normal">
                          Yes
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="hw-3-no" />
                        <FormLabel htmlFor="hw-3-no" className="font-normal">
                          No
                        </FormLabel>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="current_grade"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What is their current grade?</FormLabel>
                  <FormDescription>If you don't know, find out by the next session</FormDescription>
                  <FormControl>
                    <Input placeholder="e.g., B+" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="concerns"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Any other concerns about the student going forward?</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional concerns or notes..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="performance_rating"
              rules={{ required: "This field is required", min: 1, max: 10 }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rate your overall performance (1-10)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-4 animate-in fade-in-50">
            <FormField
              control={form.control}
              name="speak_with_parents"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Do you ever speak with the parents?</FormLabel>
                  <FormControl>
                    <Input placeholder="Yes, weekly / No, never / etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="concerns"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Any other concerns about the student going forward?</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional concerns or notes..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="performance_rating"
              rules={{ required: "This field is required", min: 1, max: 10 }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rate your overall performance (1-10)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-4 animate-in fade-in-50">
            <FormField
              control={form.control}
              name="current_grade"
              rules={{ required: "This field is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What is the student's current grade?</FormLabel>
                  <FormDescription>If you don't know, find out by the next session</FormDescription>
                  <FormControl>
                    <Input placeholder="e.g., A" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="performance_rating"
              rules={{ required: "This field is required", min: 1, max: 10 }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rate your overall performance (1-10)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Alert>
          <AlertDescription>
            Fill out the below after every session. Please do not lie, this form is only to better
            me and you. Thanks
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <div className="text-sm font-medium">Student: {studentName}</div>
          <div className="text-sm text-muted-foreground">Tutor: {tutorName}</div>
        </div>

        <FormField
          control={form.control}
          name="session_rating"
          rules={{ required: "Please rate the session" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>On a scale of 1-5 how did the session go?</FormLabel>
              <FormDescription>5 being the best</FormDescription>
              <FormControl>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <Button
                      key={rating}
                      type="button"
                      variant={field.value === rating ? "default" : "outline"}
                      onClick={() => field.onChange(rating)}
                      className="flex-1"
                    >
                      {rating}
                    </Button>
                  ))}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {renderRatingSection()}

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" className="flex-1">
            Submit Feedback
          </Button>
        </div>
      </form>
    </Form>
  );
}
